import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { IAddress } from 'helpers/types';
import axios from 'axios';

export interface IGetOrganizationAPIParams {
  organization_id?: string;
  searchString?: string;
  role_id?: string;
}
export interface IOrganizationAddress extends Omit<IAddress, 'id'> {
  _id?: string;
  is_deleted?: boolean;
}
export interface IOrganizations {
  _id?: string;
  organization_name: string;
  logo_url: string;
  logo_key: string;
  logo: string;
  fiscal_name: string;
  fiscal_number: string;
  email: string;
  country_code: string;
  mobile_no: string;
  fiscal_address: IOrganizationAddress | null;
  contact_us_url?: string;
  parking_details: IOrganizationAddress[];
  updated_at?: string;
  merchant_detail: {
    partner_id: string;
    private_key: string;
  };
  database_detail: {
    name: '';
    api_key: '';
  };
  privacy_policy_url?: string;
  geotab_credentials?: {
    database_name?: string;
    user_name?: string;
    password?: string;
    path?: string;
  };
}
export interface IOrganizationDetails extends IOrganizations {
  logo_data: {
    id: string;
    url: string;
    type: string;
    is_deleted: boolean;
    updated_at: string;
    created_at: string;
    __v: string;
  };
  is_deleted: string;
  password: string;
  parking_details: IOrganizationAddress[];
  updated_at: string;
  created_at: string;
  __v: string;
}
export interface IAddOrganization {
  organization_detail: {
    organization_name: string;
    fiscal_name: string;
    fiscal_number: string;
    email: string;
    country_code: string;
    mobile_no: string;
    fiscal_address: IAddress | null;
    logo?: string;
    contact_us_url?: string;
    privacy_policy_url?: string;
    parking_ids: string[];
  };
  merchant_detail?: {
    private_key: string;
    partner_id: string;
  };
  geotab_credentials?: {
    database_name?: string;
    user_name?: string;
    password?: string;
    path?: string;
  };
}
export interface IEditOrganizationRequest {
  organization_detail: {
    org_id: string;
    organization_name: string;
    fiscal_name: string;
    email: string;
    country_code: string;
    mobile_no: string;
    fiscal_address: IOrganizationAddress | null;
    logo?: string;
    contact_us_url?: string;
    privacy_policy_url?: string;
    parking_ids: string[];
    geotab_credentials?: {
      database_name?: string;
      user_name?: string;
      password?: string;
      path?: string;
    } | null;
  };
  merchant_detail?: {
    private_key: string;
    partner_id: string;
  };
  geotab_credentials?: {
    database_name?: string;
    user_name?: string;
    password?: string;
    path?: string;
  };
}
export interface IUploadImageRequestParam {
  request: {
    file_name: string;
    file_type: string;
    file_size: number;
  };
  file: File;
}
export interface IEditOrganizationParkinfParams {
  address_title: string;
  parking_id?: string;
  city: string;
  state: string;
  country: string;
  address1: string;
  address2?: string;
  zipcode: string;
}
interface IOrganizationState {
  organizations: IOrganizations[];
  editedOrganization: IOrganizationDetails | IOrganizations | null;
  totalCount: number;
}
interface IOrganizationResponse {
  organizationDetails: IOrganizations[];
  record_count: number;
}
const initialState: IOrganizationState = {
  organizations: [],
  editedOrganization: null,
  totalCount: 0,
};
const organizationSlice = createSlice({
  name: 'Organization',
  initialState: initialState,
  reducers: {
    setOrganizations: (state, { payload }: { payload: IOrganizationResponse }) => {
      state.organizations = [...payload.organizationDetails];
      state.totalCount = payload.record_count;
    },
    setEditedOrganization: (state, { payload }: { payload: IOrganizations | null }) => {
      state.editedOrganization = payload;
    },
  },
});

export const getUsersOrganization =
  (
    payload: {
      params?: IGetOrganizationAPIParams;
      searchString?: string;
      offset?: number;
      limit?: number;
    },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { params, searchString, offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchString ? `&searchString=${encodeURIComponent(searchString)}` : ''
      }`;
      const { data: responseData } = await api.get(`/admin/org${queryString}`, { signal });
      dispatch(setOrganizations(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getOrganizationUploadLogoLocation =
  (payload: { params: IUploadImageRequestParam }) => async () => {
    try {
      const {
        params: { request, file },
      } = payload;
      const constfile = file;
      const { data: responseData } = await api.get('/admin/assets/get/org/logo', {
        params: { ...request },
      });
      await axios.put(`${responseData?.data?.url}`, constfile);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const AddOrganizationAPI = (payload: { request: IAddOrganization }) => async () => {
  try {
    const { request } = payload;
    const { data: responseData } = await api.post('/admin/organization', request);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const EditOrganizationAPI =
  (payload: { request: IEditOrganizationRequest; org_id: string }) => async () => {
    try {
      const { request, org_id } = payload;
      const { data: responseData } = await api.post(`/admin/org/${org_id}`, request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const setEditOrganizationData =
  (payload: { request: IOrganizations | IOrganizationDetails }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      dispatch(setEditedOrganization(request));
      return Promise.resolve(true);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const deleteOrganization = (payload: { org_id: string }) => async () => {
  try {
    const { org_id } = payload;
    const { data } = await api.delete(`/admin/org/${org_id}`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getOrganizationData = (payload: { org_id: string }) => async (dispatch: Dispatch) => {
  try {
    const { org_id } = payload;
    const {
      data: { data: responseData },
    } = await api.get(`/admin/org/${org_id}`);

    dispatch(setEditedOrganization(responseData[0]));
    return Promise.resolve(responseData[0]);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addOrganizationParkingDetails =
  (payload: { request: IEditOrganizationParkinfParams }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const {
        data: { data: responseData },
      } = await api.post(`/admin/org/parking`, request);
      return Promise.resolve(responseData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const editOrganizationParkingDetails =
  (payload: { request: IEditOrganizationParkinfParams }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const {
        data: { data: responseData },
      } = await api.post(`/admin/organization/parking`, request);
      return Promise.resolve(responseData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const removeOrganizationParking =
  (payload: { params: { organization_id: string; parking_id: string } }) => async () => {
    try {
      const { params } = payload;
      const {
        data: { data: responseData },
      } = await api.delete(`/admin/remove/parking`, { params: { ...params } });
      return Promise.resolve(responseData[0]);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getOrganizationOptions =
  (payload: { request: { company_id?: string } }) => async () => {
    const { request } = payload;
    try {
      const {
        data: { data: responseData },
      } = await api.post(`/admin/org/org-names`, { ...request });

      return Promise.resolve(responseData);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const { setOrganizations, setEditedOrganization } = organizationSlice.actions;
export default organizationSlice.reducer;
